import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import { Button, Typography } from '@/ui';
import { track } from '@/utils';

import { groupAppointmentsByMonth } from './groupAppointmentsByMonth';

import type { DecodedBrowserAppointment } from '@/features/appointments/types';
import type { FC } from 'react';

import { MemberCancelSession } from '@/components/MentoCal/MemberCancelSession';
import { MemberRescheduleSession } from '@/components/MentoCal/MemberRescheduleSession';
import AppointmentRowSkeleton from '@/features/appointments/AppointmentRowSkeleton';
import MemberAppointmentRow from '@/features/appointments/MemberAppointmentRow';
import { appointmentActionSession } from '@/features/appointments/appointmentActionSession';
import { useNativeAppointmentRows } from '@/features/appointments/useNativeAppointmentRows';

// /m/appointments — Member surface for native Moments Appointments.
//
// This page owns three things:
//   1. layout (header, loading skeletons, error state, empty state),
//   2. grouping the hook's filtered+sorted appointments into month sections
//      via `groupAppointmentsByMonth`, and
//   3. the cancel ↔ reschedule dialog handoff for MentoCal.
//
// Everything else — the RTK query, the ticking clock, the booking / lifecycle
// / endAt filter, and the (startAt, id) sort — lives inside
// `useNativeAppointmentRows`. Sharing the hook with /sessions/upcoming keeps
// the two Member surfaces in sync by construction: a rule added to the hook
// reaches both.
//
// The RTK codec discards the `userId` cache-key discriminant when composing
// the request URL, so the wire request carries no `user_id`, `member_id`, or
// `series_id` param. Backend `AppointmentsController#index` composes
// `viewer_scope` from the current user's role:
//   - admin viewer (T12 Bug #5 confirmed every staging coach also carries
//     `admin`): viewer_person_id only. Moments returns every appointment the
//     viewer participates in as either coach or member — the correct union
//     for a Coach + Coachee on this page.
//   - member viewer without coach_profile: member_person_id = viewer, so
//     Moments returns their coachee-side appointments only.
//   - coach viewer without admin: coach_person_id = viewer, so Moments
//     returns their coach-side appointments. On /m/appointments this means
//     a non-admin coach who also has coaches assigned to them will not see
//     their coachee-side rows through this page today; the empty-state link
//     from /sessions/upcoming keeps them pointed at the same URL.
//
// Cancel and reschedule flows delegate to the existing MentoCal dialogs,
// which own INV-4 (Series-fence propagation) and INV-5 (immediate pre-submit
// detail read). The adapter `appointmentActionSession` maps the Appointment
// into the `Session` shape those dialogs require.
const MemberAppointments: FC = () => {
  const { user } = useCurrentUser();
  const { nativeAppointments, isError, isLoading, refetch } =
    useNativeAppointmentRows({ userId: user.id });

  const rows = useMemo(
    () => groupAppointmentsByMonth(nativeAppointments),
    [nativeAppointments],
  );

  const [openCancel, setOpenCancel] =
    useState<DecodedBrowserAppointment | null>(null);
  const [openReschedule, setOpenReschedule] =
    useState<DecodedBrowserAppointment | null>(null);

  useEffect(() => {
    track('Viewed Member Appointments Page');
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Mento | Upcoming appointments';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  const closeCancel = () => setOpenCancel(null);
  const closeReschedule = () => setOpenReschedule(null);

  return (
    <main className="mx-auto flex w-full max-w-[960px] flex-col gap-6 px-4 py-8 md:py-12">
      <div className="flex flex-col gap-1">
        <Typography variant="h4">Upcoming appointments</Typography>
        <Typography className="text-cement-400" variant="body2">
          Sessions scheduled via Mento
        </Typography>
      </div>

      {isLoading && (
        <div
          aria-label="Loading upcoming appointments"
          className="flex flex-col gap-4"
        >
          {['skeleton-1', 'skeleton-2', 'skeleton-3', 'skeleton-4'].map(
            (id) => (
              <AppointmentRowSkeleton key={id} />
            ),
          )}
        </div>
      )}

      {isError && (
        <div
          className="flex flex-col items-start gap-3 rounded-xl border border-cement-300 bg-white p-6"
          role="alert"
        >
          <Typography variant="h6">
            We couldn&apos;t load your appointments
          </Typography>
          <Typography className="text-cement-400" variant="body2">
            Try again in a moment.
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-cement-300 bg-white p-6">
          <Typography variant="h6">
            You have no upcoming appointments.
          </Typography>
          <Typography className="text-cement-400" variant="body2">
            When you book a session with your coach it will show up here.
          </Typography>
          <Link className="text-sage-500 underline" to="/m/sessions/upcoming">
            View your other sessions
          </Link>
        </div>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className="flex flex-col gap-8">
          {rows.map(({ month, items }) => (
            <section
              key={month}
              aria-label={month}
              className="flex flex-col gap-4"
            >
              <Typography className="text-cement-400" variant="subtitle2">
                {month}
              </Typography>
              <div className="flex flex-col gap-3">
                {items.map((appointment) => (
                  <MemberAppointmentRow
                    key={appointment.id}
                    appointment={appointment}
                    onCancel={() => setOpenCancel(appointment)}
                    onReschedule={() => setOpenReschedule(appointment)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {openCancel ? (
        <MemberCancelSession
          close={closeCancel}
          session={appointmentActionSession(openCancel)}
          userId={user.id}
          onCancelled={closeCancel}
          onRescheduleInstead={() => {
            // Hand off to reschedule for the same appointment without
            // re-fetching (no mutation happened yet). Capture the
            // appointment before nulling so React batches both state sets.
            const next = openCancel;
            setOpenCancel(null);
            setOpenReschedule(next);
          }}
        />
      ) : null}
      {openReschedule ? (
        <MemberRescheduleSession
          close={closeReschedule}
          session={appointmentActionSession(openReschedule)}
          onRescheduled={closeReschedule}
        />
      ) : null}
    </main>
  );
};

export default MemberAppointments;
